import React, { useState } from 'react';
import { 
  TextField, 
  Typography, 
  Button,
  Box,
  Snackbar,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: 'none',
      },
    },
  },
  contactSection: {
    background: '#1e3a5f',
    color: 'white',
    padding: '80px',
    marginTop: '60px',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '50px 30px',
    },
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr',
    gap: '80px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  contactInfo: {
    position: 'relative',
    zIndex: 2,
  },
  sectionTitle: {
    fontSize: '48px',
    marginBottom: '30px',
    fontWeight: 700,
    color: '#ffffff',
    fontFamily: '"Georgia", serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
    },
  },
  sectionDescription: {
    fontSize: '18px',
    lineHeight: 1.7,
    marginBottom: '40px',
    opacity: 0.9,
    color: '#ffffff',
    fontFamily: '"Segoe UI", sans-serif',
  },
  contactDetailMag: {
    marginBottom: '20px',
    fontSize: '16px',
    fontFamily: '"Segoe UI", sans-serif',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  contactDetailIcon: {
    color: '#d4145a',
    marginRight: '10px',
  },
  formContainer: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
  },
  contactFormMag: {
    display: 'grid',
    gap: '25px',
    width: '100%',
  },
  formRowMag: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '25px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formField: {
    width: '100%',
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      padding: '18px',
      background: 'rgba(255,255,255,0.1)',
      border: '2px solid rgba(255,255,255,0.2)',
      borderRadius: '0',
      color: 'white',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '16px',
      transition: 'all 0.3s',
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0',
      '&::placeholder': {
        color: 'rgba(255,255,255,0.5)',
        opacity: 1,
      },
    },
    '& .MuiOutlinedInput-root:hover': {
      border: '2px solid rgba(255,255,255,0.3)',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      border: '2px solid #d4145a',
      background: 'rgba(255,255,255,0.15)',
      outline: 'none',
    },
    '& .MuiFormHelperText-root': {
      color: '#d4145a',
      marginLeft: '5px',
      marginTop: '5px',
    },
  },
  submitButton: {
    background: '#d4145a',
    color: 'white',
    padding: '18px 50px',
    border: '2px solid #d4145a',
    fontSize: '14px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: 600,
    borderRadius: '0',
    justifySelf: 'start',
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      background: 'transparent',
      color: '#d4145a',
    },
  },
  successMessage: {
    backgroundColor: '#4caf50',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  errorText: {
    color: '#f44336',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
  },
  buttonProgress: {
    color: '#ffffff',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  snackbar: {
    backgroundColor: '#4caf50',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(0.5),
  },
  errorSnackbar: {
    backgroundColor: '#f44336',
  },
  snackbarIcon: {
    marginRight: theme.spacing(1),
  },
  snackbarMessage: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
}));

function ContactSection() {
  const classes = useStyles();
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  // Form errors
  const [errors, setErrors] = useState({});
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Set loading state
    setIsSubmitting(true);
    
    try {
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Show success message
      setShowSuccessAlert(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      console.log('Form data submitted:', formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setShowErrorAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Close alerts
  const handleCloseAlert = (alertType) => {
    if (alertType === 'success') {
      setShowSuccessAlert(false);
    } else {
      setShowErrorAlert(false);
    }
  };

  return (
    <section className={classes.contactSection} id="contact">
      <div className={classes.contactGrid}>
        <div className={classes.contactInfo}>
          <Typography variant="h2" className={classes.sectionTitle}>
            Get in Touch
          </Typography>
          <Typography className={classes.sectionDescription}>
            Ready to discuss your legal needs? Schedule a consultation with our expert team today.
          </Typography>
          
          <div className={classes.contactDetailMag}>
            <span className={classes.contactDetailIcon}>📧</span> mail@legalsutra.co.in
          </div>
          <div className={classes.contactDetailMag}>
            <span className={classes.contactDetailIcon}>📞</span> +91 9911512400
          </div>
          <div className={classes.contactDetailMag}>
            <span className={classes.contactDetailIcon}>📍</span> Noida-201304, India
          </div>
        </div>
        
        <div className={classes.formContainer}>
          <form className={classes.contactFormMag} onSubmit={handleSubmit}>
            <div className={classes.formRowMag}>
              <TextField
                required
                name="firstName"
                placeholder="Your Name"
                variant="outlined"
                className={classes.formField}
                value={formData.firstName}
                onChange={handleChange}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                InputProps={{
                  disableUnderline: true
                }}
              />
              
              <TextField
                required
                name="email"
                placeholder="Email Address"
                type="email"
                variant="outlined"
                className={classes.formField}
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                InputProps={{
                  disableUnderline: true
                }}
              />
            </div>
            
            <div className={classes.formRowMag}>
              <TextField
                name="phone"
                placeholder="Phone Number"
                variant="outlined"
                className={classes.formField}
                value={formData.phone}
                onChange={handleChange}
                InputProps={{
                  disableUnderline: true
                }}
              />
              
              <TextField
                name="subject"
                placeholder="Company"
                variant="outlined"
                className={classes.formField}
                value={formData.subject}
                onChange={handleChange}
                error={Boolean(errors.subject)}
                helperText={errors.subject}
                InputProps={{
                  disableUnderline: true
                }}
              />
            </div>
            
            <TextField
              required
              name="message"
              placeholder="How can we help you?"
              multiline
              minRows={4}
              variant="outlined"
              className={classes.formField}
              value={formData.message}
              onChange={handleChange}
              error={Boolean(errors.message)}
              helperText={errors.message}
              InputProps={{
                disableUnderline: true
              }}
            />
            
            <Box position="relative">
              <Button 
                type="submit"
                variant="contained"
                className={classes.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Send Message'}
              </Button>
              {isSubmitting && (
                <CircularProgress size={24} className={classes.buttonProgress} />
              )}
            </Box>
          </form>
        </div>
      </div>
      
      {/* Success Alert */}
      <Snackbar 
        open={showSuccessAlert} 
        autoHideDuration={6000} 
        onClose={() => handleCloseAlert('success')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <div className={classes.snackbar}>
          <div className={classes.snackbarMessage}>
            <CheckIcon className={classes.snackbarIcon} />
            <span>Your message has been sent successfully! We'll get back to you soon.</span>
          </div>
          <IconButton size="small" color="inherit" onClick={() => handleCloseAlert('success')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </Snackbar>
      
      {/* Error Alert */}
      <Snackbar 
        open={showErrorAlert} 
        autoHideDuration={6000} 
        onClose={() => handleCloseAlert('error')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <div className={`${classes.snackbar} ${classes.errorSnackbar}`}>
          <div className={classes.snackbarMessage}>
            <ErrorIcon className={classes.snackbarIcon} />
            <span>There was a problem sending your message. Please try again later.</span>
          </div>
          <IconButton size="small" color="inherit" onClick={() => handleCloseAlert('error')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </Snackbar>
    </section>
  );
}

export default ContactSection;